import { useState, useMemo } from 'react'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined'
import Table from '../common/table/Table'
import TableCell from '../common/table/TableCell'
import {
    CONTEXT_DIMENSION_KEYS,
    contextDimensionTitle,
    contextBucketLabel,
} from '../../helpers/dataHelpers/contextDimensionLabels'

// "Context" — the neighbourhood profile of the top performers' households /
// deelnemers / activiteiten (from CBS). Mirrors BenchmarkActivitiesSection: a
// collapsible table (reusing the bd-acttab-* styles) where each row is a context
// kenmerk that opens to its quartile breakdown. The caller's own bucket is
// highlighted. Renders nothing when no context dimension has data.

const QUARTILE_ORDER = ['Q1', 'Q2', 'Q3', 'Q4']

const COLUMNS = [
    { key: 'label', label: '', searchable: false, headerClassName: 'bd-acttab-head-label' },
    { key: 'pct', label: 'Aandeel koplopers', searchable: false, headerClassName: 'bd-acttab-head-pct' },
]

const Chevron = ({ open }) =>
    open ? <KeyboardArrowUpOutlinedIcon fontSize='small' /> : <KeyboardArrowDownOutlinedIcon fontSize='small' />

const toggleInSet = (set, key) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
}

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

const getItemKey = (item) => (item.kind === 'dim' ? `dim-${item.key}` : `bucket-${item.dimKey}-${item.key}`)

const BenchmarkContextSection = ({ explanation, showCallerHighlight = true }) => {
    const [expandedDims, setExpandedDims] = useState(() => new Set())

    const dimsWithData = useMemo(() => {
        if (!explanation) return []
        return CONTEXT_DIMENSION_KEYS
            .map(dim => ({ dim, block: explanation[dim] }))
            .filter(({ block }) => block?.rows?.length && (block.coverage?.top || 0) > 0)
    }, [explanation])

    const flattenedRows = useMemo(() => {
        const out = []
        dimsWithData.forEach(({ dim, block }) => {
            const lead = block.rows.reduce((best, r) => (r.topPct > best.topPct ? r : best), block.rows[0])
            out.push({ kind: 'dim', key: dim, topPct: lead.topPct })
            if (!expandedDims.has(dim)) return

            const sorted = [...block.rows].sort(
                (a, b) => QUARTILE_ORDER.indexOf(a.key) - QUARTILE_ORDER.indexOf(b.key)
            )
            sorted.forEach(r => out.push({ kind: 'bucket', dimKey: dim, key: r.key, topPct: r.topPct }))
        })
        return out
    }, [dimsWithData, expandedDims])

    if (!explanation) return null
    if (dimsWithData.length === 0) return null

    const callerDims = (showCallerHighlight && explanation.callerDims) || {}

    const toggleDim = (key) => setExpandedDims(prev => toggleInSet(prev, key))

    const rowClassNameHandler = (item) => {
        if (item.kind === 'dim') {
            const isOpen = expandedDims.has(item.key)
            const isCaller = (callerDims[item.key] || []).length > 0
            return `bd-acttab-row bd-acttab-row--type${isCaller ? ' bd-acttab-row--caller' : ''}${isOpen ? ' bd-acttab-row--open' : ''}`
        }
        const isCaller = (callerDims[item.dimKey] || []).includes(item.key)
        return `bd-acttab-row bd-acttab-row--subtype${isCaller ? ' bd-acttab-row--caller' : ''}`
    }

    const rowPropsHandler = (item) =>
        item.kind === 'dim'
            ? { 'data-testid': `bd-ctxtab-row-dim-${item.key}`, 'data-row-key': item.key, onClick: () => toggleDim(item.key) }
            : { 'data-testid': `bd-ctxtab-row-bucket-${item.dimKey}-${item.key}`, 'data-row-key': `${item.dimKey}-${item.key}` }

    const renderRowHandler = (item) => {
        if (item.kind === 'dim') {
            const isOpen = expandedDims.has(item.key)
            return (
                <>
                    <TableCell className='bd-acttab-cell-label'>
                        <Chevron open={isOpen} />
                        <span>{contextDimensionTitle(item.key)}</span>
                    </TableCell>
                    <TableCell className='bd-acttab-cell-pct'>{item.topPct}%</TableCell>
                </>
            )
        }
        return (
            <>
                <TableCell className='bd-acttab-cell-label bd-acttab-cell-label--subtype'>
                    <span>{capitalize(contextBucketLabel(item.dimKey, item.key))}</span>
                </TableCell>
                <TableCell className='bd-acttab-cell-pct'>{item.topPct}%</TableCell>
            </>
        )
    }

    return (
        <div className='bd-acttab-block'>
            <h2 className='bd-section-heading'>Context</h2>
            <p className='bd-section-sub'>
                In wat voor buurten werken de organisaties die boven het benchmark presteren? Klik een
                kenmerk open voor de verdeling over de buurten.
            </p>
            <Table
                columns={COLUMNS}
                items={flattenedRows}
                renderRow={renderRowHandler}
                getItemKey={getItemKey}
                getRowClassName={rowClassNameHandler}
                rowProps={rowPropsHandler}
                showSearch={false}
                tableClassName='bd-acttab'
            />
        </div>
    )
}

export default BenchmarkContextSection
